import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { HeroSection } from "@/components/HeroSection";
import { IconStrip } from "@/components/IconStrip";
import { ActionTiles } from "@/components/ActionTiles";
import { HowItWorks } from "@/components/HowItWorks";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ProductIcon } from "@/components/ProductIcon";
import { getFeaturedProducts, useCatalog } from "@/data/catalog";
import { HOME_FAQ } from "@/data/faq";

export default function HomePage() {
  useCatalog();
  const featured = getFeaturedProducts(8);

  return (
    <>
      <Seo
        title="Langganan Digital Lebih Mudah"
        description="Nofina Movie, toko langganan digital yang praktis dan transparan. Pilih platform, tentukan paket, lihat harga, lalu pesan lewat WhatsApp."
      />
      <HeroSection />
      <IconStrip />
      <ActionTiles />

      <section className="mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-nm-text sm:text-3xl">
              Produk pilihan
            </h2>
            <p className="mt-2 text-nm-muted">
              Platform populer — ketuk untuk memilih paket langsung di katalog.
            </p>
          </div>
          <Link
            to="/produk"
            data-testid="featured-see-all"
            className="hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-nm-red hover:text-nm-red-hover sm:inline-flex"
          >
            Lihat semua
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 xs:grid-cols-3 sm:grid-cols-4 lg:grid-cols-4">
          {featured.map((product) => (
            <Link
              key={product.slug}
              to={`/produk/${product.slug}`}
              data-testid={`featured-tile-${product.slug}`}
              className="group flex items-center gap-3 rounded-xl border border-nm-border bg-nm-surface p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-nm-red/40"
            >
              <ProductIcon product={product} className="h-11 w-11 shrink-0" />
              <span className="min-w-0 truncate text-sm font-semibold text-nm-text">
                {product.name}
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-6 sm:hidden">
          <Link
            to="/produk"
            className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-nm-border bg-nm-surface font-semibold text-nm-text"
          >
            Lihat Semua Produk
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <HowItWorks />

      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-nm-text sm:text-3xl">
            Pertanyaan umum
          </h2>
          <p className="mt-2 text-nm-muted">
            Hal-hal yang sering ditanyakan sebelum memesan.
          </p>
        </div>
        <div className="mt-8">
          <FAQAccordion items={HOME_FAQ} testIdPrefix="home-faq" />
        </div>
        <div className="mt-6 text-center">
          <Link
            to="/bantuan"
            className="text-sm font-semibold text-nm-red hover:text-nm-red-hover"
          >
            Lihat semua pertanyaan
          </Link>
        </div>
      </section>

      <section className="border-t border-nm-border bg-nm-bg2">
        <div className="mx-auto max-w-7xl px-4 py-14 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-nm-text sm:text-3xl">
            Siap mulai berlangganan?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-nm-muted">
            Buka katalog, pilih platform dan paket, lalu lanjutkan pesanan lewat
            WhatsApp.
          </p>
          <div className="mt-7">
            <Link
              to="/produk"
              data-testid="home-final-cta"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-nm-red px-8 font-semibold text-white shadow-lg shadow-nm-red/25 transition-colors hover:bg-nm-red-hover"
            >
              Lihat Semua Produk
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
